import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { HowItWorks } from '@/components/b2b/HowItWorks';
import { WhyOphir } from '@/components/b2b/WhyOphir';
import { SOLUTIONS, INSTITUTIONAL_SECTORS, DISCLAIMER } from '@/data/b2b';

export default function InstitutionalBuyers() {
  return (
    <>
      <Seo title="Institutional Buyers | Wholesale & Procurement Solutions | Ophir Supply"
        description="Dependable wholesale sourcing, procurement and distribution support for hospitality, healthcare, education, non-profit, government and disaster-relief organizations across North America." />
      <PageHero label="Institutional Buyers" title="Wholesale & procurement solutions for institutional buyers"
        intro="Dependable sourcing for organizations that purchase at scale. From hospitality and healthcare to educational institutions, non-profits, government purchasing and emergency-response organizations, we help procurement teams identify suitable supply options for one-time or recurring purchasing requirements."
        image="/images/ophir/institutional/hero.webp"
        imageAlt="Modern distribution center interior with organized pallets and a procurement professional" />

      <div className="bg-white border-b border-ophir-grey">
        <div className="container-ophir py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p className="text-[15px] text-ophir-navy font-medium">Purchasing at scale? Submit your requirements to our procurement team.</p>
          <OphirButton to="/request-bulk-quote" className="px-5 py-2.5 text-[14px]">Request a Bulk Quote</OphirButton>
        </div>
      </div>

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {INSTITUTIONAL_SECTORS.map((slug, i) => {
              const s = SOLUTIONS[slug];
              return (
                <FadeIn key={slug} delay={(i % 3) * 0.05}>
                  <Link to={s.path} className="group block h-full rounded-2xl border border-ophir-grey bg-ophir-ivory-soft overflow-hidden shadow-ophir hover:shadow-ophir-hover transition-shadow">
                    <div className="aspect-[16/10] overflow-hidden bg-ophir-ivory">
                      <img src={s.image} alt={s.imageAlt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                    </div>
                    <div className="p-6">
                      <h2 className="font-serif text-[20px] text-ophir-navy-deep">{s.label}</h2>
                      <p className="mt-2 text-[14.5px] leading-relaxed text-ophir-muted line-clamp-3">{s.intro}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-ophir-navy group-hover:text-ophir-navy-deep">Explore <ArrowRight size={16} /></span>
                    </div>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <HowItWorks dark />
      <WhyOphir />

      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-10"><p className="text-[13px] leading-relaxed text-ophir-muted">{DISCLAIMER}</p></div>
      </section>

      <section className="navy-gradient">
        <div className="container-ophir py-16 text-center">
          <h2 className="font-serif text-[28px] sm:text-[36px] text-white max-w-2xl mx-auto leading-tight">One procurement partner. Multiple categories.</h2>
          <p className="mt-4 text-[16px] text-white/75 max-w-xl mx-auto">Reduce the complexity of dealing with numerous vendors by submitting your purchasing requirements through Ophir Supply.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <OphirButton variant="gold" to="/request-bulk-quote">Request a Bulk Quote</OphirButton>
            <OphirButton variant="secondary" to="/contact" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">Contact Our Procurement Team</OphirButton>
          </div>
        </div>
      </section>
    </>
  );
}
